import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FileBarChart2, Gauge, Briefcase, Banknote, AlertTriangle, ShieldCheck, GraduationCap, FileText, FileSpreadsheet, CalendarClock, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REPORTS = [
  { key: 'executive', name: 'Executive Report', desc: 'KPI board, RAG statuses and pending approvals', icon: Gauge, tone: 'bg-blue-100 dark:bg-blue-500/10 text-blue-600' },
  { key: 'projects', name: 'Project Report', desc: 'All projects with status, budget and stage-gate progress', icon: Briefcase, tone: 'bg-violet-100 dark:bg-violet-500/10 text-violet-600' },
  { key: 'financial', name: 'Financial Report', desc: 'Income, expenses, net position and outstanding invoices', icon: Banknote, tone: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600' },
  { key: 'risk', name: 'Risk Report', desc: 'Full risk register ranked by score with mitigations', icon: AlertTriangle, tone: 'bg-rose-100 dark:bg-rose-500/10 text-rose-600' },
  { key: 'compliance', name: 'Compliance Report', desc: 'SETA/QCTO/ISO/POPIA/OHS register, findings and CAPAs', icon: ShieldCheck, tone: 'bg-amber-100 dark:bg-amber-500/10 text-amber-600' },
  { key: 'learners', name: 'Learner Report', desc: 'Learner register with attendance, assessments and placements', icon: GraduationCap, tone: 'bg-teal-100 dark:bg-teal-500/10 text-teal-600' },
];

const DAYS = [
  { v: 'mon', l: 'Monday' }, { v: 'tue', l: 'Tuesday' }, { v: 'wed', l: 'Wednesday' },
  { v: 'thu', l: 'Thursday' }, { v: 'fri', l: 'Friday' }, { v: 'sat', l: 'Saturday' }, { v: 'sun', l: 'Sunday' },
];
const TIMES = ['05:00', '05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00'];

const WeeklySchedule = () => {
  const [cfg, setCfg] = useState(null);
  const [recipientsText, setRecipientsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reports/schedule`).then(res => {
      setCfg(res.data);
      setRecipientsText((res.data.recipients || []).join(', '));
    }).catch(() => {});
  }, []);

  if (!cfg) return null;

  const toggleModule = (key) => {
    const has = cfg.modules.includes(key);
    setCfg({ ...cfg, modules: has ? cfg.modules.filter(m => m !== key) : [...cfg.modules, key] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const recipients = recipientsText.split(',').map(r => r.trim()).filter(Boolean);
      const res = await axios.put(`${API}/reports/schedule`, { ...cfg, recipients });
      setCfg(res.data);
      setRecipientsText((res.data.recipients || []).join(', '));
      toast.success('Weekly schedule saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/reports/schedule/send-now`);
      if (res.data.sent_count > 0) toast.success(`Report pack emailed to ${res.data.sent_count} recipient(s)`);
      else toast.error(res.data.error || 'No emails sent — check recipients and SMTP');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="!p-5" data-testid="weekly-schedule-card">
      <CardContent className="p-0 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold">Scheduled Weekly Report Pack</p>
              <p className="text-xs text-muted-foreground">Automatically emails the executive report pack as PDF attachments</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="weekly-enabled" className="text-sm">Enabled</Label>
            <Switch id="weekly-enabled" checked={cfg.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="weekly-enabled-toggle" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Day</Label>
            <Select value={cfg.day} onValueChange={(v) => setCfg({ ...cfg, day: v })}>
              <SelectTrigger data-testid="weekly-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAYS.map(d => <SelectItem key={d.v} value={d.v} data-testid={`weekly-day-${d.v}`}>{d.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Time (SAST)</Label>
            <Select value={cfg.time} onValueChange={(v) => setCfg({ ...cfg, time: v })}>
              <SelectTrigger data-testid="weekly-time-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMES.map(t => <SelectItem key={t} value={t} data-testid={`weekly-time-${t}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Recipients (comma-separated emails — defaults to MD if empty)</Label>
          <Input value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)}
            placeholder="md@ikworx.co.za, exec@ikworx.co.za" data-testid="weekly-recipients-input" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Reports to include</Label>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {REPORTS.map(r => (
              <label key={r.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={cfg.modules.includes(r.key)} onCheckedChange={() => toggleModule(r.key)}
                  data-testid={`weekly-module-${r.key}`} />
                {r.name}
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button size="sm" className="rounded-xl" onClick={save} disabled={saving} data-testid="weekly-save-btn">
            {saving ? 'Saving…' : 'Save Schedule'}
          </Button>
          <Button size="sm" variant="outline" className="rounded-xl gap-1.5" onClick={sendNow} disabled={sending}
            data-testid="weekly-send-now-btn">
            <Send className="h-3.5 w-3.5" /> {sending ? 'Sending…' : 'Send Now'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function ReportsPage() {
  const [busy, setBusy] = useState(null);

  const download = async (key, format) => {
    setBusy(`${key}-${format}`);
    try {
      const res = await axios.get(`${API}/reports/${key}`, { params: { format }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${key}_report.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error('Report generation failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" data-testid="reports-title">
          <FileBarChart2 className="h-7 w-7 text-primary" /> Reports
        </h1>
        <p className="text-muted-foreground mt-1">One-click PDF & Excel reports generated from live data</p>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {REPORTS.map(r => {
          const RIcon = r.icon;
          return (
            <Card key={r.key} className="!p-5 hover:shadow-md transition-shadow" data-testid={`report-card-${r.key}`}>
              <CardContent className="p-0 space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${r.tone}`}>
                    <RIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{r.desc}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 gap-1.5 rounded-xl" disabled={busy === `${r.key}-pdf`}
                    onClick={() => download(r.key, 'pdf')} data-testid={`report-pdf-${r.key}`}>
                    <FileText className="h-4 w-4" /> {busy === `${r.key}-pdf` ? 'Generating…' : 'PDF'}
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 gap-1.5 rounded-xl" disabled={busy === `${r.key}-xlsx`}
                    onClick={() => download(r.key, 'xlsx')} data-testid={`report-xlsx-${r.key}`}>
                    <FileSpreadsheet className="h-4 w-4" /> {busy === `${r.key}-xlsx` ? 'Generating…' : 'Excel'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <WeeklySchedule />
    </div>
  );
}
